// ProtectedRoute — copied verbatim.md.
// Pure consumer of the auth store — does NOT fire its own refresh. Bootstrap
// is driven by useAuthBootstrap via <RootLayout />.
import { Navigate, Outlet, useLocation } from 'react-router';
import { useTranslation } from 'react-i18next';
import { useAuthStore } from '../stores/auth.js';
import { LoadingState } from '../ui/index.js';

// WHY (#1259): external call-center agents are confined to the intake page.
//   This is the single confinement seam — any internal route (and `/`) is
//   redirected back here for a `call_center_agent`. The intake path itself
//   is exempt to avoid a redirect loop; the intake route's own gate then
//   renders the page. The REAL security boundary is the server (#1258);
//   this is UX confinement so an external agent never browses internal CRM.
const CALL_CENTER_AGENT_ROLE = 'call_center_agent';
const CALL_CENTER_INTAKE_PATH = '/call-center/intake';

export function ProtectedRoute() {
  const { t } = useTranslation(['common']);
  const hydrating = useAuthStore((s) => s.hydrating);
  const bootstrapThrottled = useAuthStore((s) => s.bootstrapThrottled);
  const accessToken = useAuthStore((s) => s.accessToken);
  const user = useAuthStore((s) => s.user);
  const loc = useLocation();
  // WHY: bootstrapThrottled is "still hydrating" from the user's view — the
  // long-tail retry in useAuthBootstrap is waiting on the @Throttle window
  // to expire. Redirecting to /login here would punish a valid cookie holder
  // for transient back-pressure they didn't cause.
  if (bootstrapThrottled) return <LoadingState text={t('common:states.restoringSession')} />;
  if (hydrating) return <LoadingState />;
  if (!accessToken)
    return (
      <Navigate to={`/login?redirect=${encodeURIComponent(loc.pathname + loc.search)}`} replace />
    );
  if (user?.role?.name === CALL_CENTER_AGENT_ROLE && loc.pathname !== CALL_CENTER_INTAKE_PATH) {
    return <Navigate to={CALL_CENTER_INTAKE_PATH} replace />;
  }
  return <Outlet />;
}
